/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import "./index.css";
import React, { useCallback, useMemo, useRef } from "react";
import * as ReactDOM from "react-dom";
import { Typography13 } from "@slid/slid-ips";

import { $getNearestNodeFromDOMNode, $getSelection, LexicalEditor, LexicalNode } from "lexical";
import { $createHorizontalRuleNode } from "components/NewEditor/nodes/HorizontalRuleNode";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $setBlocksType } from "@lexical/selection";
import { $createHeadingNode } from "@lexical/rich-text";
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import { ReactComponent as MathIcon } from "../../images/icons/math.svg";
import { $createCodeNode } from "@lexical/code";
import useModal from "../../hooks/useModal";
import { $createSlidParagraphNode } from "components/NewEditor/nodes/SlidParagraphNode";

import { InsertEquationDialog } from "../EquationsPlugin";
import { InsertImageDialog } from "../ImagesPlugin";
import { InsertTableDialog } from "../TablePlugin";
import { InsertLinkPreviewDialog } from "../SlidLinkPreviewPlugin";
import { ReactComponent as ParagraphIcon } from "../../images/icons/dropdown-paragraph.svg";
import { ReactComponent as Heading1Icon } from "../../images/icons/dropdown-heading1.svg";
import { ReactComponent as Heading2Icon } from "../../images/icons/dropdown-heading2.svg";
import { ReactComponent as Heading3Icon } from "../../images/icons/dropdown-heading3.svg";
import { ReactComponent as BulletListIcon } from "../../images/icons/dropdown-bullet.svg";
import { ReactComponent as NumberedListIcon } from "../../images/icons/dropdown-number.svg";
import { ReactComponent as CheckListIcon } from "../../images/icons/dropdown-check.svg";
import { ReactComponent as TableIcon } from "../../images/icons/table.svg";
import { ReactComponent as DividerIcon } from "../../images/icons/divider.svg";
import { ReactComponent as ImageIcon } from "../../images/icons/image.svg";
import { ReactComponent as LinkPreviewIcon } from "../../images/icons/link-preview.svg";
import { ReactComponent as CodeBlockIcon } from "../../images/icons/code-block.svg";
import i18n from "config/i18n/i18n";
import { MenuOption } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { SlidLexicalAddButtonPlugin } from "../SlidLexicalAddButtonPlugin";
import { useSlidLexicalAddButtonStore } from "../SlidLexicalAddButtonPlugin/utils/useSlidLexicalAddButtonStore";
import { useLocation } from "react-router-dom";

const { t } = i18n;

class ComponentPickerOption extends MenuOption {
  // What shows up in the editor
  title: string;
  // Icon for display
  icon?: JSX.Element;
  // For extra searching.
  keywords: Array<string>;
  // TBD
  keyboardShortcut?: string;
  // What happens when you select this option?
  onSelect: () => void;

  constructor(
    title: string,
    options: {
      icon?: JSX.Element;
      keywords?: Array<string>;
      keyboardShortcut?: string;
      onSelect: () => void;
    }
  ) {
    super(title);
    this.title = title;
    this.keywords = options.keywords || [];
    this.icon = options.icon;
    this.keyboardShortcut = options.keyboardShortcut;
    this.onSelect = options.onSelect.bind(this);
  }
}

function ComponentPickerMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: ComponentPickerOption;
}) {
  let className = "item";
  if (isSelected) {
    className += " selected";
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={"typeahead-item-" + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {option.icon}
      <Typography13 text={option.title} color={"--gray15"} type="span" />
    </li>
  );
}

const ADD_BUTTON_CLASSNAME = "add-block-menu";
const DRAGGABLE_BLOCK_MENU_CLASSNAME = "draggable-block-menu";

function isOnMenu(element: HTMLElement): boolean {
  const isOnMenu = !!element.closest(`.${ADD_BUTTON_CLASSNAME}`) || !!element.closest(`.${DRAGGABLE_BLOCK_MENU_CLASSNAME}`);
  return isOnMenu;
}

type ShowModal = ReturnType<typeof useModal>[1];

function getBaseOptions(editor: LexicalEditor, showModal: ShowModal, targetBlockElem: HTMLElement | null) {
  return [
    new ComponentPickerOption(t("paragraph", { ns: "EditorToolbar" }), {
      icon: <ParagraphIcon />,
      keywords: ["normal", "paragraph", "p", "text"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            const newParagraph = $createSlidParagraphNode();
            node.insertAfter(newParagraph);
            newParagraph.select();
          }
        }),
    }),
    ...([1, 2, 3] as const).map(
      (n) =>
        new ComponentPickerOption(t(`heading${n}`, { ns: "EditorToolbar" }), {
          icon: n === 1 ? <Heading1Icon /> : n === 2 ? <Heading2Icon /> : <Heading3Icon />,
          keywords: ["heading", "header", `h${n}`],
          onSelect: () =>
            editor.update(() => {
              const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
              if (node) {
                const newParagraph = $createSlidParagraphNode();
                node.insertAfter(newParagraph);
                newParagraph.select();
                const selection = $getSelection();
                $setBlocksType(selection, () => $createHeadingNode(`h${n}`));
              }
            }),
        })
    ),
    new ComponentPickerOption(t("numberedList", { ns: "EditorToolbar" }), {
      icon: <NumberedListIcon />,
      keywords: ["numbered list", "ordered list", "ol"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            const newParagraph = $createSlidParagraphNode();
            node.insertAfter(newParagraph);
            newParagraph.select();
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          }
        }),
    }),
    new ComponentPickerOption(t("bulletList", { ns: "EditorToolbar" }), {
      icon: <BulletListIcon />,
      keywords: ["bulleted list", "unordered list", "ul"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            const newParagraph = $createSlidParagraphNode();
            node.insertAfter(newParagraph);
            newParagraph.select();
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          }
        }),
    }),
    new ComponentPickerOption(t("checkList", { ns: "EditorToolbar" }), {
      icon: <CheckListIcon />,
      keywords: ["check list", "todo list"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            const newParagraph = $createSlidParagraphNode();
            node.insertAfter(newParagraph);
            newParagraph.select();
            editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
          }
        }),
    }),
    new ComponentPickerOption(t("table", { ns: "EditorToolbar" }), {
      icon: <TableIcon />,
      keywords: ["table", "grid", "spreadsheet", "rows", "columns"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            node.selectEnd();
            showModal(t("table.title", { keySeparator: ".", ns: "EditorModal" }), (onClose) => <InsertTableDialog activeEditor={editor} onClose={onClose} />);
          }
        }),
    }),
    new ComponentPickerOption(t("code-block", { ns: "EditorToolbar" }), {
      icon: <CodeBlockIcon />,
      keywords: ["javascript", "python", "js", "codeblock"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            const codeNode = $createCodeNode();
            codeNode.select();
            node.insertAfter(codeNode);
          }
        }),
    }),
    new ComponentPickerOption(t("divider", { ns: "EditorToolbar" }), {
      icon: <DividerIcon />,
      keywords: ["horizontal rule", "divider", "hr"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            const newParagraph = $createSlidParagraphNode();
            node.insertAfter(newParagraph);
            const horizontalRule = $createHorizontalRuleNode();
            newParagraph.replace(horizontalRule);
          }
        }),
    }),
    new ComponentPickerOption(t("math", { ns: "EditorToolbar" }), {
      icon: <MathIcon />,
      keywords: ["equation", "latex", "math"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            node.selectEnd();
            showModal(t("equation.title", { keySeparator: ".", ns: "EditorModal" }), (onClose) => <InsertEquationDialog activeEditor={editor} onClose={onClose} />);
          }
        }),
    }),
    new ComponentPickerOption(t("image", { ns: "EditorToolbar" }), {
      icon: <ImageIcon />,
      keywords: ["image", "photo", "picture", "file"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            node.selectEnd();
            showModal(t("image.title", { keySeparator: ".", ns: "EditorModal" }), (onClose) => <InsertImageDialog activeEditor={editor} onClose={onClose} />);
          }
        }),
    }),
    new ComponentPickerOption(t("link-preview", { ns: "EditorToolbar" }), {
      icon: <LinkPreviewIcon />,
      keywords: ["link preview", "link", "preview"],
      onSelect: () =>
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetBlockElem as Node);
          if (node) {
            const newParagraph = $createSlidParagraphNode();
            node.insertAfter(newParagraph);
            newParagraph.select();
            showModal(t("link-preview.title", { keySeparator: ".", ns: "EditorModal" }), (onClose) => <InsertLinkPreviewDialog activeEditor={editor} onClose={onClose} />);
          }
        }),
    }),
  ];
}

export default function SlidAddButtonPlugin({ anchorElem = document.body }: { anchorElem?: HTMLElement }) {
  const [editor] = useLexicalComposerContext();
  const [modal, showModal] = useModal();
  const { targetBlockElem } = useSlidLexicalAddButtonStore();
  const location = useLocation();
  const isVideoNote = location.pathname.includes("/vdocs");

  const options = useMemo(() => {
    const baseOptions = getBaseOptions(editor, showModal, targetBlockElem);

    return baseOptions;
  }, [editor, showModal, targetBlockElem]);

  const onSelectOption = useCallback(
    (selectedOption: ComponentPickerOption, targetNode: LexicalNode | null, closeMenu: () => void) => {
      editor.update(() => {
        selectedOption.onSelect();
        closeMenu();
      });
    },
    [editor]
  );

  return (
    <>
      {modal}
      <SlidLexicalAddButtonPlugin
        options={options}
        onSelectOption={onSelectOption}
        menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
          anchorElementRef.current && options.length
            ? ReactDOM.createPortal(
                <div className="typeahead-popover component-picker-menu">
                  <ul>
                    {options.map((option, i: number) => (
                      <ComponentPickerMenuItem
                        index={i}
                        isSelected={selectedIndex === i}
                        onClick={() => {
                          setHighlightedIndex(i);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                        }}
                        key={option.key}
                        option={option}
                      />
                    ))}
                  </ul>
                </div>,
                anchorElementRef.current
              )
            : null
        }
        isEditable={editor._editable}
        anchorElem={anchorElem}
        scrollWrapper={(isVideoNote ? document.getElementById("editor-container") : document.getElementById("editor-component")) || undefined}
      />
    </>
  );
}
